import { useEffect, useRef } from 'react'
import { createPortal } from 'react-dom'
import { parseCfoRouteState } from '../../parity-dev/cfo/cfoRoutes'
import { pickAnchor } from './pickAnchor'
import { DEFAULT_HIGHLIGHT, DEFAULT_POSITION, type TourStep } from '../tourTypes'

/**
 * Record-a-flow: author a tour by doing the flow once.
 *
 * Unlike ElementPicker (which blocks the click so binding an anchor never
 * triggers the app), the recorder's entire point is that the app KEEPS
 * WORKING — a capture-phase listener grades what was clicked, appends a draft
 * step, and lets the click proceed to navigate/drill/filter as normal. The
 * step is recorded against the page the click started on, which is exactly
 * where a click_anchor step must anchor.
 *
 * The author still writes the copy afterward — recording produces the
 * skeleton (anchor + route + advance), not the story. Brittle grade-D anchors
 * are recorded as-is: the rail already badges them and publish still blocks
 * on them, so the friction lands in the right place.
 */
export function FlowRecorder({ recordedCount, onRecord, onStop }: {
  recordedCount: number
  onRecord: (step: TourStep) => void
  onStop: () => void
}) {
  const lastKey = useRef<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    document.addEventListener('click', (e) => {
      const target = e.target instanceof Element ? e.target : null
      if (!target) return
      // The authoring chrome is not part of the prospect's flow.
      if (target.closest('.fg-author') || target.closest('.fg-author-rec') || target.closest('.fg-tour')) return
      const candidate = pickAnchor(target)
      // Hammer-click guard: the same element twice in a row is one step.
      const key = `${candidate.anchor.kind}:${'value' in candidate.anchor ? candidate.anchor.value : ''}`
      if (key === lastKey.current) return
      lastKey.current = key
      const route = parseCfoRouteState(window.location)
      const el = target.closest('button, a, [role="button"], [data-fg-tour], [data-testid]') ?? target
      const label = (el.getAttribute('aria-label') || el.textContent || 'this control')
        .trim().replace(/\s+/g, ' ').slice(0, 60)
      onRecord({
        id: `rec-${Date.now().toString(36)}`,
        title: label,
        route: { page: (route?.page || 'ceo') as TourStep['route']['page'] },
        anchor: candidate.anchor,
        widget: { kind: 'hotspot', body: '', highlight: DEFAULT_HIGHLIGHT, position: DEFAULT_POSITION } as TourStep['widget'],
        advance: { kind: 'click_anchor' },
        fallback: 'modal',
      } as TourStep)
    }, { capture: true, signal: controller.signal })

    window.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Escape') { e.preventDefault(); onStop() }
    }, { signal: controller.signal })

    return () => controller.abort()
  }, [onRecord, onStop])

  return createPortal(
    <div className="fg-author-rec" role="status">
      <span className="fg-author-rec__dot" aria-hidden />
      Recording — click through your flow · {recordedCount} step{recordedCount === 1 ? '' : 's'}
      <button type="button" className="fg-author-rec__stop" onClick={onStop}>Stop</button>
    </div>,
    document.body,
  )
}
